/**
 * @file Builds the column definitions for the beneficiaries table.
 */
import React, { useCallback, useMemo } from "react";
import type { ColumnType } from "antd/es/table";
import { Table } from "antd";

import { Bold, Value } from "@components/Text";
import InputDistributionAmount from "../InputDistributionAmount";
import type { BeneficiaryItem, Frequency } from "../../types";
import getDefaultAmount from "./getDefaultAmount";

const LABEL_INVESTOR = "Beneficiary";
const LABEL_EMAIL = "Email";
const LABEL_AMOUNT = "Invested Amount";
const LABEL_CLASS = "Class";
const LABEL_RATE = "Return Rate";
const LABEL_DISTRIBUTION_AMOUNT = "Amount";

/**
 * Builds the beneficiaries table columns, including the editable distribution amount column.
 */
const useColumns = (
  amountByKey: Record<string, number>,
  setCustomAmountByKey: React.Dispatch<React.SetStateAction<Record<string, number>>>,
  selectedRowKeys: React.Key[],
  distributionFrequency: Frequency,
  shouldRoundCents: boolean,
) => {
  const onAmountClear = useCallback((key: string) => {
    setCustomAmountByKey(previous => {
      const { [key]: _, ...rest } = previous;
      return rest;
    });
  }, [ setCustomAmountByKey ]);

  const onAmountChange = useCallback((key: string, value: number) => {
    if (!value) {
      return onAmountClear(key);
    }

    setCustomAmountByKey(previous => ({
      ...previous,
      [key]: value,
    }));
  }, [ setCustomAmountByKey, onAmountClear ]);

  return useMemo((): (ColumnType<BeneficiaryItem> | typeof Table.SELECTION_COLUMN)[] => ([
    {
      key: "investor",
      title: LABEL_INVESTOR,
      dataIndex: "investor",
      width: "30%",
      /**
       * Renders the beneficiary name in bold.
       */
      render: (value: string) => <Bold>{value}</Bold>,
    },
    {
      key: "email",
      title: LABEL_EMAIL,
      dataIndex: "email",
      width: "30%",
    },
    {
      key: "investedAmount",
      title: LABEL_AMOUNT,
      dataIndex: "investedAmount",
      width: "10%",
      /**
       * Renders the invested amount as a formatted currency value.
       */
      render: (value: number) => <Value value={value} />,
    },
    {
      key: "className",
      title: LABEL_CLASS,
      dataIndex: "className",
      width: "10%",
    },
    {
      key: "returnRate",
      title: LABEL_RATE,
      dataIndex: "returnRate",
      width: "10%",
      /**
       * Renders the return rate as a percentage.
       */
      render: (value: number) => `${value || 0}%`,
    },
    {
      key: "amount",
      title: LABEL_DISTRIBUTION_AMOUNT,
      width: "10%",
      /**
       * Renders the editable distribution amount input for the row.
       */
      render: (_: unknown, item: BeneficiaryItem) => {
        const { key } = item;
        const valueInCents = Math.round(Number(amountByKey[key]) || 0);
        const defaultCents = getDefaultAmount(item, distributionFrequency, shouldRoundCents);
        const isCustom = valueInCents !== defaultCents;

        return (
          <InputDistributionAmount
            disabled={!selectedRowKeys.includes(key)}
            valueInCents={valueInCents}
            isCustom={isCustom}
            onChange={value => onAmountChange(key, value)}
          />
        );
      },
    },
    Table.SELECTION_COLUMN,
  ]), [ amountByKey, distributionFrequency, onAmountChange, selectedRowKeys, shouldRoundCents ]);
};

export default useColumns;
